import React from 'react';
import {CaseStudyPromo, CaseStudyPromoProps} from './CaseStudy.promo';
import styled from 'styled-components';
import {SectionIntro, SectionIntroProps} from '../../elements/section/SectionIntro';
import {
    CenteredGrid,
    GridContainer,
    VerticalSpacer,
    ImageCard,
    ImageCardProps,
    Button,
    CmsButtonProps,
    LogoList,
    LogoListProps,
    HiddenSSR,
    LightboxButtonProps,
    LightboxButton
} from '../../elements';
import {SubnavSectionConfigProps} from '../../elements/subnav/SubnavSection';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import Grid from '@material-ui/core/Grid';
import {
    OverviewGridBottomLeft,
    OverviewGridBottomLeftOptionsArray
} from '../overview/OverviewGrid.bottomleft';
import {Spotlight} from '../../wraps/spotlight/Spotlight.wrap';
import {UeThemeProvider, useProperDarkTheme, useBreakpointUp} from 'epic-ue-theme';
import {AnimateOnViewWrap} from '../../wraps/animation/AnimateOnViewWrap';

const Styled = styled.div`
    .content {
        position: relative;
        /* sit above the outline div */
        .content-inner {
            position: relative;
            z-index: 1;
        }
    }

    .outline {
        pointer-events: none;
        border: 1px solid ${(p) => p.theme.palette.text.primary};
        margin: 0 40px;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        /* sit below the content */
        z-index: 0;
    }

    .image-card .eyebrow-wrapper {
        color: ${(p) => p.theme.palette.text.secondary};
    }

    .logolist {
        margin-top: 10px;
    }
`;

export interface CaseStudySectionProps {
    accentColor?: AccentColorOptions;
    sectionConfig: SubnavSectionConfigProps;
    promo?: CaseStudyPromoProps;
    logos?: LogoListProps;
    intro?: SectionIntroProps;
    topLeftSlot?: ImageCardProps;
    topRightSlot?: ImageCardProps;
    bottomLeftSlot?: OverviewGridBottomLeftOptionsArray;
    bottomRightSlot?: ImageCardProps;
    moreBtn?: CmsButtonProps;
    hidden?: boolean;
    hideIntro?: boolean;
    hideLogos?: boolean;
    spotlightsDisabled?: boolean;
    lightboxButton?: LightboxButtonProps;
}

const noVerticalPadding = {
    paddingTop: 0,
    paddingBottom: 0
};

export const CaseStudySection = ({
    promo = {},
    logos = {},
    intro = {},
    sectionConfig = {},
    accentColor,
    topLeftSlot = {},
    topRightSlot = {},
    bottomLeftSlot,
    bottomRightSlot = {},
    moreBtn = {},
    hidden = false,
    hideIntro = false,
    hideLogos = false,
    spotlightsDisabled,
    lightboxButton
}: CaseStudySectionProps): JSX.Element | null => {
    const isSm = useBreakpointDown('sm');
    const lgUp = useBreakpointUp('lg');
    const darkTheme = useProperDarkTheme();
    if (hidden) return null;
    const topRightSlotImage = topRightSlot.image || {};
    topRightSlotImage.ratio = 'square';

    const {index, sectionName} = sectionConfig;

    const bottomRightSlotValid = Boolean(
        bottomRightSlot && bottomRightSlot.title && bottomRightSlot.image
    );
    const bottomLeftSlotValid = Boolean(
        bottomLeftSlot &&
            bottomLeftSlot.length &&
            bottomLeftSlot[0] &&
            Object.keys(bottomLeftSlot[0]).length
    );

    return (
        <UeThemeProvider theme={darkTheme}>
            <Styled className="case-study">
                {!hideIntro && (
                    <SectionIntro
                        {...intro}
                        index={index}
                        sectionName={sectionName}
                        accentColor={accentColor}
                    />
                )}
                {!hideLogos && (
                    <>
                        <LogoList {...logos} />
                        <VerticalSpacer variant="custom" spacing={6} downSmSpacing={0} />
                    </>
                )}
                <GridContainer>
                    <div className="content">
                        <HiddenSSR mdDown>
                            <AnimateOnViewWrap threshold={0}>
                                <div className="outline" />
                            </AnimateOnViewWrap>
                        </HiddenSSR>
                        <div className="content-inner">
                            {(!hideLogos || !logos.items) && (
                                <VerticalSpacer variant={isSm ? 'eyebrow' : 'section'} />
                            )}
                            <CaseStudyPromo
                                {...promo}
                                accentColor={accentColor}
                                spotlightsDisabled={spotlightsDisabled}
                            />
                            <CenteredGrid
                                justifyContent="flex-start"
                                alignItems="flex-end"
                                style={{marginBottom: 0}}>
                                <Grid item xs={12} md={7} style={{paddingBottom: 0}}>
                                    {/* On smDown we want the full card, but mdUp we need to separate the image from the tile and brow */}
                                    <HiddenSSR smDown>
                                        <VerticalSpacer
                                            variant="custom"
                                            spacing={15}
                                            downSmSpacing={0}
                                        />
                                        <AnimateOnViewWrap translateY={30}>
                                            <Spotlight
                                                image={topLeftSlot?.image}
                                                variant="medium"
                                                offsetRatio={[-0.1, 0.4]}
                                                optMaxWidth={900}
                                                disable={spotlightsDisabled}>
                                                <ImageCard
                                                    accentColor={accentColor}
                                                    image={topLeftSlot?.image}
                                                    link={topLeftSlot?.link}
                                                    caption={topLeftSlot?.caption}
                                                    optMaxWidth={900}
                                                />
                                            </Spotlight>
                                        </AnimateOnViewWrap>
                                    </HiddenSSR>
                                    <HiddenSSR mdUp>
                                        <AnimateOnViewWrap translateY={30}>
                                            <ImageCard
                                                {...topLeftSlot}
                                                description={undefined}
                                                accentColor={accentColor}
                                                optMaxWidth={900}
                                            />
                                        </AnimateOnViewWrap>
                                    </HiddenSSR>
                                </Grid>
                                <Grid item xs={1} />
                                <Grid item xs={10} md={4} style={{paddingBottom: 0}}>
                                    <HiddenSSR mdUp>
                                        <VerticalSpacer variant="section" />
                                        <AnimateOnViewWrap translateY={30}>
                                            <ImageCard
                                                {...topRightSlot}
                                                description={undefined}
                                                accentColor={accentColor}
                                                optMaxWidth={900}
                                            />
                                        </AnimateOnViewWrap>
                                    </HiddenSSR>

                                    {/* On smDown we want the full card, but mdUp we need to separate the image from the tile and brow */}
                                    <HiddenSSR smDown>
                                        <CenteredGrid
                                            justifyContent="flex-start"
                                            alignItems="flex-start">
                                            <Grid item xs={12} style={{paddingBottom: 0}}>
                                                <AnimateOnViewWrap translateY={30}>
                                                    <ImageCard
                                                        accentColor={accentColor}
                                                        image={topRightSlotImage}
                                                        link={topRightSlot?.link}
                                                        caption={topRightSlot?.caption}
                                                    />
                                                </AnimateOnViewWrap>
                                            </Grid>
                                            <Grid item xs={12} md={10} style={noVerticalPadding}>
                                                <AnimateOnViewWrap translateY={30}>
                                                    <ImageCard
                                                        title={topRightSlot.title}
                                                        eyebrow={topRightSlot.eyebrow}
                                                        link={topRightSlot.link}
                                                        description={undefined}
                                                        accentColor={accentColor}
                                                        optMaxWidth={400}
                                                    />
                                                </AnimateOnViewWrap>
                                            </Grid>
                                        </CenteredGrid>
                                    </HiddenSSR>
                                </Grid>
                            </CenteredGrid>
                            <HiddenSSR smDown>
                                <CenteredGrid style={{marginTop: 0}}>
                                    <Grid item md={1} />
                                    <Grid item xs={12} md={5} style={noVerticalPadding}>
                                        <AnimateOnViewWrap translateY={30}>
                                            <ImageCard
                                                eyebrow={topLeftSlot.eyebrow}
                                                title={topLeftSlot.title}
                                                link={topLeftSlot.link}
                                                description={undefined}
                                                accentColor={accentColor}
                                                optMaxWidth={400}
                                            />
                                        </AnimateOnViewWrap>
                                    </Grid>
                                </CenteredGrid>
                                {bottomLeftSlotValid && bottomRightSlotValid ? (
                                    <VerticalSpacer variant="custom" spacing={9} />
                                ) : (
                                    <VerticalSpacer variant="section" />
                                )}
                            </HiddenSSR>
                            <HiddenSSR mdUp>
                                <VerticalSpacer variant="section" />
                            </HiddenSSR>

                            <HiddenSSR smDown>
                                <CenteredGrid justifyContent="flex-start" alignItems="flex-start">
                                    {lgUp ? <Grid item lg={1} /> : ''}
                                    <Grid item md={5} lg={4}>
                                        {bottomLeftSlotValid && (
                                            <>
                                                <VerticalSpacer variant="custom" spacing={7} />
                                                <AnimateOnViewWrap translateY={30} duration={1.5}>
                                                    <OverviewGridBottomLeft
                                                        dynamicPageDataArray={bottomLeftSlot}
                                                        imageRatioOverride="square"
                                                        disableDescription
                                                        accentColor={accentColor}
                                                    />
                                                </AnimateOnViewWrap>
                                                <HiddenSSR mdUp>
                                                    <VerticalSpacer variant="section" />
                                                </HiddenSSR>
                                            </>
                                        )}
                                    </Grid>
                                    <Grid item md={1} />
                                    <Grid item md={5}>
                                        {bottomRightSlotValid && (
                                            <AnimateOnViewWrap translateY={30}>
                                                <Spotlight
                                                    image={bottomRightSlot?.image}
                                                    variant="big"
                                                    shape="oval"
                                                    tilt="left"
                                                    offsetRatio={[-0.9, 0.5]}
                                                    optMaxWidth={900}
                                                    disable={spotlightsDisabled}>
                                                    <ImageCard
                                                        {...bottomRightSlot}
                                                        description={undefined}
                                                        accentColor={accentColor}
                                                        optMaxWidth={900}
                                                    />
                                                </Spotlight>
                                            </AnimateOnViewWrap>
                                        )}
                                    </Grid>
                                </CenteredGrid>
                            </HiddenSSR>
                            <HiddenSSR mdUp>
                                <CenteredGrid justifyContent="flex-start" alignItems="flex-start">
                                    {bottomRightSlotValid && (
                                        <>
                                            <Grid item xs={12}>
                                                <AnimateOnViewWrap translateY={30}>
                                                    <ImageCard
                                                        {...bottomRightSlot}
                                                        description={undefined}
                                                        accentColor={accentColor}
                                                        optMaxWidth={900}
                                                    />
                                                </AnimateOnViewWrap>
                                            </Grid>
                                            <VerticalSpacer variant="section" />
                                            <Grid item xs={1} />
                                        </>
                                    )}
                                    {bottomLeftSlotValid && (
                                        <>
                                            <Grid item xs={10}>
                                                <VerticalSpacer
                                                    variant="custom"
                                                    spacing={7}
                                                    downSmSpacing={0}
                                                />
                                                <AnimateOnViewWrap translateY={30}>
                                                    <OverviewGridBottomLeft
                                                        dynamicPageDataArray={bottomLeftSlot}
                                                        imageRatioOverride="square"
                                                        disableDescription
                                                        accentColor={accentColor}
                                                    />
                                                </AnimateOnViewWrap>
                                                {isSm ? (
                                                    <VerticalSpacer variant="custom" spacing={5} />
                                                ) : (
                                                    <VerticalSpacer variant="section" />
                                                )}
                                            </Grid>
                                        </>
                                    )}
                                </CenteredGrid>
                            </HiddenSSR>

                            {moreBtn && moreBtn.label && (
                                <>
                                    {!isSm && (bottomLeftSlotValid || bottomRightSlotValid) && (
                                        <VerticalSpacer variant="section" />
                                    )}
                                    <AnimateOnViewWrap translateY={30}>
                                        <div style={{textAlign: 'center'}}>
                                            {lightboxButton?.label ? (
                                                <LightboxButton {...lightboxButton} />
                                            ) : (
                                                <Button
                                                    type="secondary"
                                                    href={moreBtn.href}
                                                    color={accentColor}>
                                                    {moreBtn.label}
                                                </Button>
                                            )}
                                        </div>
                                        <VerticalSpacer variant="feature" />
                                    </AnimateOnViewWrap>
                                </>
                            )}
                        </div>
                    </div>
                </GridContainer>
            </Styled>
        </UeThemeProvider>
    );
};
